import React from 'react';
import { MEMBERSHIP_BENEFITS } from '../../constants';
import { 
  UsersIcon, 
  AcademicCapIcon, 
  CheckBadgeIcon, 
  DocumentTextIcon, 
  CalendarIcon, 
  UserGroupIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';

const MembershipBenefitsPage: React.FC = () => {
  const getBenefitIcon = (iconName: string) => {
    const iconProps = { className: "w-8 h-8" };
    
    switch (iconName) {
      case 'users':
        return <UsersIcon {...iconProps} />;
      case 'academic-cap':
        return <AcademicCapIcon {...iconProps} />;
      case 'badge-check':
        return <CheckBadgeIcon {...iconProps} />;
      case 'document-text':
        return <DocumentTextIcon {...iconProps} />;
      case 'calendar':
        return <CalendarIcon {...iconProps} />;
      case 'user-group':
        return <UserGroupIcon {...iconProps} />;
      default:
        return <UsersIcon {...iconProps} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'general':
        return 'bg-blue-100 text-blue-600';
      case 'professional':
        return 'bg-green-100 text-green-600';
      case 'premium':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const benefitsByCategory = MEMBERSHIP_BENEFITS.reduce((acc, benefit) => {
    if (!acc[benefit.category]) {
      acc[benefit.category] = [];
    }
    acc[benefit.category].push(benefit);
    return acc;
  }, {} as Record<string, typeof MEMBERSHIP_BENEFITS>);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max section-padding">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Membership Benefits
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the comprehensive benefits and resources available to CSEAN members
            </p>
          </div>
        </div>
      </div>

      {/* Benefits by Category */}
      <div className="section-padding">
        <div className="container-max">
          {Object.entries(benefitsByCategory).map(([category, benefits]) => (
            <div key={category} className="mb-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 capitalize">
                  {category} Benefits
                </h2>
                <div className="w-24 h-1 bg-primary-500 mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {benefits.map((benefit) => (
                  <div
                    key={benefit.id}
                    className="card hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105"
                  >
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-lg mr-4 ${getCategoryColor(benefit.category)}`}>
                        {getBenefitIcon(benefit.icon)}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {benefit.title}
                        </h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(benefit.category)}`}>
                          {benefit.category}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Benefits Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Additional Member Perks
            </h2>
            <p className="text-xl text-gray-600">
              Beyond the core benefits, enjoy these exclusive advantages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Technical Reports
              </h3>
              <p className="text-gray-600 text-sm">
                Access to exclusive research and technical documentation
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Early Event Access
              </h3>
              <p className="text-gray-600 text-sm">
                Priority registration for conferences and workshops
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckBadgeIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Industry Recognition
              </h3>
              <p className="text-gray-600 text-sm">
                Verified professional credentials and member directory listing
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Member Directory
              </h3>
              <p className="text-gray-600 text-sm">
                Connect with verified cybersecurity professionals
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary-600 section-padding">
        <div className="container-max text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Unlock These Benefits?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join CSEAN today and start enjoying all these benefits while advancing your cybersecurity career.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/membership/application"
              className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              Start Application
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </a>
            <a
              href="/membership/categories"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              View Categories
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipBenefitsPage;
